/**
 * DemoIntentService — PRD-38 (Seeded-Data-Aware Demo Assistant), Phase 1
 *
 * Replaces the canned `handleDemoQuery` templates for the bounded v1 intent
 * set with answers DERIVED FROM THE SEEDED DATASET, so the assistant's figures
 * match the dashboard / Receivables / Payables surfaces to the dollar.
 *
 * MECHANISM: HYBRID (founder-approved 2026-06-15).
 *   1. DETERMINISTIC LAYER — every figure is computed here from the single
 *      source of truth, `fetchDemoFinancialData()` (the BUG-24/27/28 accessor
 *      every dashboard surface also reads). The LLM never computes a number.
 *   2. PHRASING LAYER — the computed deterministic answer is sent to the
 *      EXISTING app AI provider (Gemini, via the server `/ai/phrase` route)
 *      ONLY to reword it conversationally in the active language. If the LLM
 *      is unavailable or fails, we fall back to the deterministic template,
 *      so the assistant still answers correctly without the LLM.
 *
 * Phase-1 intents: cash position, payables, receivables, overdue invoices.
 * Out-of-scope queries get a graceful capability message (no fabricated number).
 */

import { fetchDemoFinancialData, formatCurrency, type DemoFinancialData } from './DemoDataService';
import { chatDemoCopy, getChatLanguage, type ChatDemoLanguage } from '@/lib/i18n/chat-demo-copy';
import { apiClient } from '@/lib/api-client';

/**
 * BUG-87 — hard wall-clock budgets.
 *
 * The assistant must ALWAYS settle. `apiClient` now bounds each individual
 * request, but the resolve path awaits several of them in sequence, so it also
 * carries its own end-to-end deadline: whatever goes wrong underneath, the
 * caller gets a rendered answer (or a rendered failure) rather than a promise
 * that never settles and a spinner that never stops.
 */
export const DEMO_DATA_DEADLINE_MS = 15_000;
export const DEMO_PHRASING_DEADLINE_MS = 9_000;

/** Resolve with `onTimeout()` if `p` neither resolves nor rejects in time. */
function withDeadline<T>(p: Promise<T>, ms: number, onTimeout: () => T): Promise<T> {
  return new Promise<T>((resolve) => {
    let settled = false;
    const finish = (v: T) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(v);
    };
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      resolve(onTimeout());
    }, ms);
    p.then(finish, () => finish(onTimeout()));
  });
}

export type DemoIntent =
  | 'cash_position'
  | 'payables'
  | 'receivables'
  | 'overdue'
  | 'out_of_scope';

export interface DemoIntentResult {
  /** The intent that was detected. */
  intent: DemoIntent;
  /** Final text to show the user (phrased if LLM succeeded, else deterministic). */
  text: string;
  /** True when the LLM phrasing layer produced the text; false on deterministic fallback. */
  phrased: boolean;
  /** The exact computed figures (formatted) — asserted in evals to equal seeded data. */
  figures: Record<string, string>;
  /** Which financial AI agent "owns" this intent (for the chat header). */
  primaryAgent: string;
}

// ---------------------------------------------------------------------------
// Intent detection (bilingual EN + ES keyword matching)
// ---------------------------------------------------------------------------

const includesAny = (q: string, words: string[]) => words.some((w) => q.includes(w));

/**
 * Map a raw query to one of the Phase-1 intents, or `out_of_scope`.
 * Order matters: "overdue" is checked before generic "receivables" because an
 * overdue question is a more specific receivables question.
 */
export function detectDemoIntent(query: string): DemoIntent {
  const q = (query || '').toLowerCase();

  // Overdue / collections (most specific first). Note: bare "cobrar" is NOT
  // here because "cuentas por cobrar" is receivables, not overdue — we require
  // genuine overdue/collections markers (vencid-, atrasad-, collect, cobranza).
  if (
    includesAny(q, [
      'overdue', 'past due', 'late invoice', 'collect first', 'collect', 'collections', 'aging',
      'vencid', 'atrasad', 'cobranza',
    ])
  ) {
    return 'overdue';
  }

  // Cash position / liquidity / working capital
  if (
    includesAny(q, [
      'cash position', 'cash', 'liquidity', 'runway', 'working capital', 'how much money',
      'efectivo', 'liquidez', 'flujo de caja', 'capital de trabajo', 'posición de efectivo', 'posicion de efectivo',
    ])
  ) {
    return 'cash_position';
  }

  // Receivables / what we are owed (checked before payables: "owed to me" is a
  // receivables question, and the word "owe" alone is ambiguous).
  if (
    includesAny(q, [
      'receivable', 'receivables', 'owed to', 'owed me', 'owe me', 'owed to me',
      'outstanding invoice', 'accounts receivable',
      'por cobrar', 'cuentas por cobrar', 'me deben', 'facturas pendientes',
    ])
  ) {
    return 'receivables';
  }

  // Payables / what we owe. Use directional "owe" phrases only, so "owed to me"
  // (receivables, handled above) never falls through here.
  if (
    includesAny(q, [
      'payable', 'payables', 'i owe', 'we owe', 'bills', 'vendor', 'supplier payment',
      'pay out', 'due this week',
      'por pagar', 'cuentas por pagar', 'pagar', 'proveedor', 'factura por pagar', 'debemos',
    ])
  ) {
    return 'payables';
  }

  return 'out_of_scope';
}

// ---------------------------------------------------------------------------
// Deterministic answer builders (one per intent, EN + ES)
// Each returns the answer text AND the exact figures used (for evals).
// ---------------------------------------------------------------------------

interface DeterministicAnswer {
  text: string;
  figures: Record<string, string>;
  primaryAgent: string;
}

/**
 * Cash position — the FULL cash-FLOW picture (BUG-87).
 *
 * PRD-38 replaced the pre-existing rich cash-flow answer with two sentences
 * (balance = receivables − payables) and orphaned `generateCashFlowInsight`.
 * The reporter caught it: "it doesn't really tell me what the cash flow is,
 * whereas we had that earlier." A balance is a position, not a flow — the
 * aging split, the overdue names and what's actually due are the answer.
 *
 * This restores that depth by reusing the SAME `chatDemoCopy().cashFlow`
 * i18n template the old handler used, while keeping PRD-38's guarantee intact:
 * every figure is still computed here, deterministically, from
 * `fetchDemoFinancialData()` — the single source the dashboard cards read.
 */
function buildCashPosition(data: DemoFinancialData, lang: ChatDemoLanguage): DeterministicAnswer {
  const balance = data.cashPosition.currentBalance;
  const receivables = data.cashPosition.totalReceivables;
  const payables = data.cashPosition.totalPayables;
  const netPosition = receivables - payables;
  const aging = data.receivables.aging;
  const topInvoices = (data.receivables.topInvoices || []).slice(0, 3);

  // The first three keys are the PRD-38 dashboard-match contract (asserted in
  // PRD-38-evidence.test.ts); the rest are the restored cash-FLOW detail.
  const figures: Record<string, string> = {
    cashPosition: formatCurrency(balance),
    totalReceivables: formatCurrency(receivables),
    totalPayables: formatCurrency(payables),
    netPosition: formatCurrency(netPosition),
    overdue: formatCurrency(data.receivables.overdue),
    agingThirty: formatCurrency(aging.thirtyDays),
    agingSixty: formatCurrency(aging.sixtyDays),
    // BUG-113: 61-90 used to have nowhere to go and was folded into 90+.
    agingNinety: formatCurrency(aging.ninetyDays),
    agingNinetyPlus: formatCurrency(aging.ninetyPlus),
    dueThisWeek: formatCurrency(data.payables.dueThisWeek),
    dueSoon: formatCurrency(data.payables.dueSoon),
  };
  topInvoices.forEach((inv, i) => {
    figures[`invoice${i + 1}Buyer`] = inv.buyerName;
    figures[`invoice${i + 1}Amount`] = formatCurrency(inv.amount);
    figures[`invoice${i + 1}DaysOverdue`] = String(inv.daysOverdue);
  });

  const text = chatDemoCopy(lang).cashFlow({
    workingCapital: figures.cashPosition,
    receivablesTotal: figures.totalReceivables,
    payablesTotal: figures.totalPayables,
    netPosition: figures.netPosition,
    overdue: figures.overdue,
    agingThirty: figures.agingThirty,
    agingSixty: figures.agingSixty,
    agingNinety: figures.agingNinety,
    agingNinetyPlus: figures.agingNinetyPlus,
    topInvoices: topInvoices.map((inv) => ({
      buyerName: inv.buyerName,
      amount: formatCurrency(inv.amount),
      daysOverdue: inv.daysOverdue,
    })),
    dueThisWeek: figures.dueThisWeek,
    dueSoon: figures.dueSoon,
  });

  return { text, figures, primaryAgent: 'Poniente' };
}

/**
 * Payables — the AP position, not a one-line balance (BUG-101).
 *
 * `generatePayablesInsight` in UnifiedChatRouter held the "rich" payables answer
 * and had 0 call sites, orphaned by PRD-38's `detectDemoIntent` exactly as
 * `generateCashFlowInsight` was in BUG-87. Its content was NOT carried over,
 * and deliberately so: that answer was a hardcoded string naming invented US
 * suppliers ("SupplyCo Industries", "TechVendor LLC", "GlobalParts Inc") with
 * invented figures ($634,890 total, $4,840/month savings). Restoring it would
 * have put fabricated names in front of the MX demo audience while the
 * dashboard beside it showed the real bills — the BUG-71 / BUG-90 defect class.
 *
 * So the depth here is rebuilt from the seeded bills only. No discount terms,
 * no early-payment savings, no vendor-negotiation figures: nothing in the
 * dataset supports them.
 */
function buildPayables(data: DemoFinancialData, lang: ChatDemoLanguage): DeterministicAnswer {
  const copy = chatDemoCopy(lang);
  const total = data.payables.total;
  const dueThisWeek = data.payables.dueThisWeek;
  const dueSoon = data.payables.dueSoon;
  const bills = (data.payables.topBills || []).slice(0, 3);

  const figures: Record<string, string> = {
    payablesTotal: formatCurrency(total),
    dueThisWeek: formatCurrency(dueThisWeek),
    dueSoon: formatCurrency(dueSoon),
  };
  bills.forEach((b, i) => {
    figures[`bill${i + 1}Label`] = billLabel(b, data);
    figures[`bill${i + 1}Amount`] = formatCurrency(b.amount);
    figures[`bill${i + 1}DueDate`] = formatDate(b.dueDate, lang);
  });

  const billList = bills.length
    ? bills
        .map(
          (b, i) =>
            `${i + 1}. ` +
            copy.payablesBillLine({
              label: billLabel(b, data),
              amount: formatCurrency(b.amount),
              dueDate: formatDate(b.dueDate, lang),
              status: dueStatus(b.dueDate, lang),
            })
        )
        .join('\n\n')
    : copy.payablesNoBills;

  const text = copy.payables({
    total: figures.payablesTotal,
    dueThisWeek: figures.dueThisWeek,
    dueSoon: figures.dueSoon,
    billList,
    billCount: bills.length,
  });

  return { text, figures, primaryAgent: 'Poniente' };
}

/**
 * A bill's display label.
 *
 * BUG-101: on production every seeded bill's `supplierName` resolves to the
 * TENANT'S OWN company name — `bills.supplier_id` points at a supplier row
 * carrying the buyer's name — so naming the supplier would render "Forjas y
 * Mecanizados del Bajío — $521,722" on Forjas' own payables answer, i.e. the
 * company billing itself. The real supplier identity survives only in the bill
 * number (`TERN-MTY-2026-0892`) and the description.
 *
 * So the label falls back to the bill number whenever the "supplier" is the
 * tenant. This self-corrects the moment the seed is fixed — it is a guard, not
 * a permanent decision. The underlying seed defect is raised separately.
 */
function billLabel(
  bill: { supplierName?: string; billNumber: string },
  data: DemoFinancialData
): string {
  const supplier = (bill.supplierName || '').trim();
  const tenant = (data.company?.name || '').trim();
  const normalize = (s: string) => s.toLowerCase().replace(/[.,]/g, '').replace(/\s+/g, ' ');
  if (!supplier || normalize(supplier) === normalize(tenant)) return bill.billNumber;
  return supplier;
}

function formatDate(iso: string, lang: ChatDemoLanguage): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  // Deterministic: fixed locale + UTC, never the runner's timezone.
  return d.toLocaleDateString(lang === 'es' ? 'es-MX' : 'en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * Whether a bill is already past its due date.
 *
 * Returns `Vigente` / `Current`, NOT `Programada` / `Scheduled`. This function
 * knows exactly one thing: `due < Date.now()`. "Scheduled" asserts that a
 * payment has been ARRANGED, which nothing here established — a finance
 * director reading `Programada` would reasonably infer their treasury team had
 * already queued it. That is a date comparison wearing a workflow state's
 * costume, the same shape as claiming agents ran when none did (BUG-104/105).
 * `Vigente` is the app's established counterpart to `Vencida` (see
 * `recv.current`, `analytics.insights.agingCurrent`) and claims only what is
 * true.
 *
 * Compares against the bill's own due date only — no wall-clock arithmetic that
 * would make the answer drift between two runs on the same data. `Date.now()`
 * is read once per call and only to classify, never to produce a figure.
 */
function dueStatus(iso: string, lang: ChatDemoLanguage): string {
  const due = new Date(iso).getTime();
  // An unparseable due date is NOT evidence the bill is current. Returning
  // `Vigente` here would fail toward "everything is fine" on exactly the
  // malformed record someone needs to notice — the same over-claim as
  // `Programada`, in the branch that matters most. Say we do not know.
  if (Number.isNaN(due)) return lang === 'es' ? 'Sin fecha' : 'No due date';
  const overdue = due < Date.now();
  if (lang === 'es') return overdue ? 'Vencida' : 'Vigente';
  return overdue ? 'Past due' : 'Current';
}

/**
 * Receivables — the AR position with its aging split and the named priorities
 * (BUG-101).
 *
 * This was two sentences ("You have $X in receivables. Of that, $Y is current
 * and $Z is overdue.") — 95 characters against a tenant carrying 15 invoices,
 * a full aging split and 5 named overdue buyers. The depth lived in
 * `generateReceivablesInsight`, which PRD-38 orphaned to 0 call sites.
 *
 * Unlike the payables orphan, that one WAS genuinely data-driven, so its
 * content is carried over intact — reusing the same `chatDemoCopy()` templates
 * it used, so there is ONE receivables answer in both languages rather than two
 * divergent copies. That is the shape BUG-87 settled on for cash flow.
 */
function buildReceivables(data: DemoFinancialData, lang: ChatDemoLanguage): DeterministicAnswer {
  const copy = chatDemoCopy(lang);
  const total = data.receivables.total;
  const overdue = data.receivables.overdue;
  const aging = data.receivables.aging;
  const overdueInvoices = (data.receivables.topInvoices || []).filter((i) => i.daysOverdue > 0);
  const top = overdueInvoices.slice(0, 3);

  const pct = (part: number) => (total > 0 ? Math.round((part / total) * 100) : 0);

  const figures: Record<string, string> = {
    receivablesTotal: formatCurrency(total),
    overdue: formatCurrency(overdue),
    current: formatCurrency(aging.current),
    agingThirty: formatCurrency(aging.thirtyDays),
    agingSixty: formatCurrency(aging.sixtyDays),
    agingNinety: formatCurrency(aging.ninetyDays),
    agingNinetyPlus: formatCurrency(aging.ninetyPlus),
  };
  top.forEach((inv, i) => {
    figures[`invoice${i + 1}Buyer`] = inv.buyerName;
    figures[`invoice${i + 1}Amount`] = formatCurrency(inv.amount);
    figures[`invoice${i + 1}DaysOverdue`] = String(inv.daysOverdue);
  });

  const priorityList = top.length
    ? top
        .map(
          (inv, i) =>
            `${i + 1}. ` +
            copy.receivablesInvoiceLine({
              buyer: inv.buyerName,
              amount: formatCurrency(inv.amount),
              daysOverdue: inv.daysOverdue,
              invoiceNumber: inv.invoiceNumber,
              action: copy.receivablesInvoiceAction(inv.daysOverdue),
              priority: copy.receivablesPriority(inv.daysOverdue),
            })
        )
        .join('\n\n')
    : copy.receivablesNoOverdue;

  const text = copy.receivables({
    totalOutstanding: figures.receivablesTotal,
    current: figures.current,
    currentPct: pct(aging.current),
    thirty: figures.agingThirty,
    thirtyPct: pct(aging.thirtyDays),
    sixty: figures.agingSixty,
    sixtyPct: pct(aging.sixtyDays),
    // BUG-113: `ninety` used to be handed the ">60" bucket and printed under a
    // "90+ days" label. It is now the real 61-90 band, with 90+ its own line.
    ninety: figures.agingNinety,
    ninetyPct: pct(aging.ninetyDays),
    ninetyPlus: figures.agingNinetyPlus,
    ninetyPlusPct: pct(aging.ninetyPlus),
    priorityList,
    overdueCount: overdueInvoices.length,
    overdueAmount: figures.overdue,
  });

  return { text, figures, primaryAgent: 'Windrose' };
}

function buildOverdue(data: DemoFinancialData, lang: ChatDemoLanguage): DeterministicAnswer {
  const overdue = data.receivables.overdue;
  const top = (data.receivables.topInvoices || []).filter((i) => i.daysOverdue > 0).slice(0, 3);

  const figures: Record<string, string> = { overdueTotal: formatCurrency(overdue) };
  top.forEach((inv, i) => {
    figures[`invoice${i + 1}Buyer`] = inv.buyerName;
    figures[`invoice${i + 1}Amount`] = formatCurrency(inv.amount);
    figures[`invoice${i + 1}DaysOverdue`] = String(inv.daysOverdue);
  });

  if (top.length === 0) {
    const text =
      lang === 'es'
        ? `No tienes facturas vencidas en este momento. El total vencido es ${figures.overdueTotal}.`
        : `You have no overdue invoices right now. Total overdue is ${figures.overdueTotal}.`;
    return { text, figures, primaryAgent: 'Windrose' };
  }

  const lines = top
    .map((inv, i) =>
      lang === 'es'
        ? `${i + 1}. ${inv.buyerName} — ${formatCurrency(inv.amount)} (${inv.daysOverdue} días de atraso, factura ${inv.invoiceNumber})`
        : `${i + 1}. ${inv.buyerName} — ${formatCurrency(inv.amount)} (${inv.daysOverdue} days overdue, invoice ${inv.invoiceNumber})`
    )
    .join('\n');

  const text =
    lang === 'es'
      ? `Tienes ${figures.overdueTotal} en facturas vencidas. Las prioritarias son:\n${lines}`
      : `You have ${figures.overdueTotal} in overdue invoices. The priorities are:\n${lines}`;
  return { text, figures, primaryAgent: 'Windrose' };
}

/**
 * Compute the deterministic answer for an in-scope intent from seeded data.
 * Pure & synchronous given the data — this is what evals assert against.
 */
export function computeDeterministicAnswer(
  intent: Exclude<DemoIntent, 'out_of_scope'>,
  data: DemoFinancialData,
  lang: ChatDemoLanguage
): DeterministicAnswer {
  switch (intent) {
    case 'cash_position':
      return buildCashPosition(data, lang);
    case 'payables':
      return buildPayables(data, lang);
    case 'receivables':
      return buildReceivables(data, lang);
    case 'overdue':
      return buildOverdue(data, lang);
  }
}

// ---------------------------------------------------------------------------
// Empty-state + out-of-scope copy
// ---------------------------------------------------------------------------

function emptyStateText(lang: ChatDemoLanguage): string {
  return lang === 'es'
    ? 'Todavía no veo datos financieros sembrados para esta cuenta, así que no puedo darte cifras. Carga o siembra datos de demostración para continuar.'
    : "I don't see any seeded financial data for this account yet, so I can't give you figures. Load or seed demo data to continue.";
}

/**
 * BUG-87 — shown when the data layer does not answer inside its budget.
 * A visible, honest failure the user can act on, never an endless spinner and
 * never a fabricated figure.
 */
export function dataUnavailableText(lang: ChatDemoLanguage): string {
  return lang === 'es'
    ? 'No pude obtener tus datos financieros en este momento, así que no voy a darte una cifra que podría estar equivocada. Vuelve a intentarlo en unos segundos.'
    : "I couldn't reach your financial data just now, so I won't quote a figure that might be wrong. Please try again in a moment.";
}

/**
 * True when `phrased` still contains every computed figure verbatim.
 *
 * The phrasing layer is allowed to reword, never to drop or alter a number.
 * The restored cash-flow answer carries ~15 figures, which is enough to hit the
 * phrase route's token cap — a truncated reply would silently lose the aging
 * split or an invoice. If anything is missing we discard the phrasing and ship
 * the deterministic text.
 */
function preservesAllFigures(phrased: string, figures: Record<string, string>): boolean {
  return Object.values(figures).every((v) => phrased.includes(v));
}

export function outOfScopeText(lang: ChatDemoLanguage): string {
  return lang === 'es'
    ? 'Puedo ayudarte con tu posición de efectivo, cuentas por pagar, cuentas por cobrar y facturas vencidas. ¿Sobre cuál te gustaría saber más?'
    : 'I can help with your cash position, payables, receivables, and overdue invoices. Which of those would you like to know about?';
}

// ---------------------------------------------------------------------------
// Phrasing layer (LLM = phrasing-only, with deterministic fallback)
// ---------------------------------------------------------------------------

/**
 * Ask the server to reword `deterministicAnswer` conversationally via the
 * existing Gemini provider. Returns the phrased text, or null on ANY failure
 * (so the caller falls back to the deterministic text). The LLM never sees
 * raw data and never computes — it only rewords the already-correct draft.
 */
async function phraseWithLLM(
  intent: Exclude<DemoIntent, 'out_of_scope'>,
  deterministicAnswer: string,
  figures: Record<string, string>,
  lang: ChatDemoLanguage
): Promise<string | null> {
  try {
    const res = await apiClient.post<{ text?: string }>('/ai/phrase', {
      intent,
      language: lang,
      deterministicAnswer,
      figures,
    });
    const text = res?.text?.trim();
    return text ? text : null;
  } catch {
    // 503 (no key), 502 (provider failed/empty), network error -> fall back.
    return null;
  }
}

// ---------------------------------------------------------------------------
// Public entry point
// ---------------------------------------------------------------------------

export interface ResolveOptions {
  /** When false, skip the LLM entirely (used by deterministic-only tests). */
  usePhrasing?: boolean;
  /** Override language detection (defaults to live SettingsContext language). */
  language?: ChatDemoLanguage;
}

/**
 * Resolve a Phase-1 demo query end-to-end:
 *  - detect intent
 *  - out-of-scope -> graceful capability message (no number)
 *  - in-scope -> compute deterministic figures from seeded data, then phrase
 *    via the LLM (falling back to the deterministic text on any failure)
 */
export async function resolveDemoIntent(
  query: string,
  opts: ResolveOptions = {}
): Promise<DemoIntentResult> {
  const lang: ChatDemoLanguage = opts.language ?? getChatLanguage();
  const intent = detectDemoIntent(query);

  if (intent === 'out_of_scope') {
    return {
      intent,
      text: outOfScopeText(lang),
      phrased: false,
      figures: {},
      primaryAgent: 'Zephyr',
    };
  }

  // BUG-87: bounded. `fetch` has no default timeout, so a hung upstream used to
  // leave this promise pending forever — which pinned the chat's `isProcessing`
  // flag true (its `finally` never ran), spun the spinner indefinitely and
  // disabled the very quick-query buttons the user was clicking.
  const TIMED_OUT = Symbol('demo-data-timeout');
  const data = await withDeadline<DemoFinancialData | null | typeof TIMED_OUT>(
    fetchDemoFinancialData(),
    DEMO_DATA_DEADLINE_MS,
    () => TIMED_OUT,
  );

  if (data === TIMED_OUT) {
    return {
      intent,
      text: dataUnavailableText(lang),
      phrased: false,
      figures: {},
      primaryAgent: 'Zephyr',
    };
  }

  // Empty / no-data state — never fabricate figures.
  const hasData =
    !!data &&
    (data.receivables.total > 0 ||
      data.payables.total > 0 ||
      Math.abs(data.cashPosition.currentBalance) > 0);

  if (!hasData) {
    return {
      intent,
      text: emptyStateText(lang),
      phrased: false,
      figures: {},
      primaryAgent: 'Zephyr',
    };
  }

  const deterministic = computeDeterministicAnswer(intent, data as DemoFinancialData, lang);

  if (opts.usePhrasing === false) {
    return {
      intent,
      text: deterministic.text,
      phrased: false,
      figures: deterministic.figures,
      primaryAgent: deterministic.primaryAgent,
    };
  }

  // Bounded too: the phrasing layer is a nice-to-have, never a reason to stall.
  const phrased = await withDeadline<string | null>(
    phraseWithLLM(intent, deterministic.text, deterministic.figures, lang),
    DEMO_PHRASING_DEADLINE_MS,
    () => null,
  );

  // Only accept phrasing that kept every figure intact.
  const usable = phrased != null && preservesAllFigures(phrased, deterministic.figures);

  return {
    intent,
    text: usable ? (phrased as string) : deterministic.text,
    phrased: usable,
    figures: deterministic.figures,
    primaryAgent: deterministic.primaryAgent,
  };
}
